import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { API_BASE } from '../../config/api';
import { PageShell } from '../../components/admin/PageShell';
import { StatCard } from '../../components/admin/StatCard';
import { DataCard } from '../../components/admin/DataCard';
import { EmptyState } from '../../components/admin/EmptyState';
import { Skeleton, SkeletonCard } from '../../components/admin/Skeleton';
import { Badge } from '../../components/admin/Badge';
import { ActivityFeed } from '../../components/admin/ActivityFeed';
import { QuickActions } from '../../components/admin/QuickActions';

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, closed: 0 });
  const [recentSubmissions, setRecentSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Analytics state
  const [leadsMetrics, setLeadsMetrics] = useState(null);
  const [websiteSnapshot, setWebsiteSnapshot] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const backendUrl = API_BASE;

  useEffect(() => {
    fetchDashboardData();
    fetchAnalyticsData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const response = await fetch(`${backendUrl}/api/admin/forms`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
        return;
      }

      if (!response.ok) throw new Error('Failed to fetch data');

      const data = await response.json();
      const forms = data.forms || [];

      // Calculate stats - use lead_status with fallback to status
      const total = forms.length;
      const getStatus = (f) => f.lead_status || f.status || 'new';
      const newCount = forms.filter(f => getStatus(f) === 'new').length;
      const contacted = forms.filter(f => getStatus(f) === 'contacted').length;
      const closed = forms.filter(f => getStatus(f) === 'closed').length;

      setStats({ total, new: newCount, contacted, closed });
      
      // Get recent 10 submissions
      const recent = forms
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 10);
      setRecentSubmissions(recent);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalyticsData = async () => {
    try {
      setAnalyticsLoading(true);
      const token = localStorage.getItem('admin_token');
      
      if (!token) {
        return;
      }

      // Fetch leads analytics (priority - always from DB)
      const leadsResponse = await fetch(`${backendUrl}/api/admin/analytics/leads?days=30`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (leadsResponse.ok) {
        const leadsData = await leadsResponse.json();
        setLeadsMetrics(leadsData.metrics);
      }

      // Fetch website snapshot (secondary - from GA4 if available)
      const snapshotResponse = await fetch(`${backendUrl}/api/admin/analytics/website-snapshot`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (snapshotResponse.ok) {
        const snapshotData = await snapshotResponse.json();
        setWebsiteSnapshot(snapshotData.snapshot);
      }
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      // Don't set error - analytics are secondary
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const getFormTypeLabel = (type) => {
    const labels = {
      'general_inquiry': 'General Inquiry',
      'appointment_booking': 'Appointment',
      'campus_tour': 'Campus Tour'
    };
    return labels[type] || type;
  };

  const getStatusColor = (status) => {
    const colors = {
      'new': 'bg-blue-100 text-blue-800',
      'contacted': 'bg-yellow-100 text-yellow-800',
      'closed': 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <AdminLayout>
        <PageShell title="Dashboard" subtitle="Loading dashboard data...">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        </PageShell>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <PageShell 
        title="Dashboard" 
        subtitle="Overview of all form submissions and analytics"
      >

        {/* SECTION A - LEADS METRICS (TOP PRIORITY) */}
        {leadsMetrics && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-bold text-[#111827] mb-4">Leads Overview</h2>
              
              {/* Leads KPI Cards - Using StatCard Component */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <StatCard
                  label="Leads Today"
                  value={leadsMetrics.today?.count || 0}
                  change={leadsMetrics.today?.change}
                  changeLabel="vs yesterday"
                  iconBg="bg-blue-100"
                  iconColor="text-blue-600"
                  icon={
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  }
                />
                <StatCard
                  label="This Week"
                  value={leadsMetrics.this_week?.count || 0}
                  change={leadsMetrics.this_week?.change}
                  changeLabel="vs last week"
                  iconBg="bg-purple-100"
                  iconColor="text-purple-600"
                  icon={
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  }
                />
                <StatCard
                  label="This Month"
                  value={leadsMetrics.this_month?.count || 0}
                  change={leadsMetrics.this_month?.change}
                  changeLabel="vs last month"
                  iconBg="bg-emerald-100"
                  iconColor="text-emerald-600"
                  icon={
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  }
                />
              </div>

              {/* Leads Breakdown by Type - Using DataCard */}
              {leadsMetrics.breakdown_by_type?.this_month && Object.keys(leadsMetrics.breakdown_by_type.this_month).length > 0 && (
                <DataCard title="Leads by Type (This Month)" className="mb-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {Object.entries(leadsMetrics.breakdown_by_type.this_month).map(([type, count], idx) => {
                      const colors = [
                        'text-blue-600',
                        'text-purple-600',
                        'text-emerald-600'
                      ];
                      const colorClass = colors[idx % colors.length];
                      return (
                        <div key={type} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:shadow-sm transition-all">
                          <span className="text-sm font-semibold text-gray-700">{type}</span>
                          <span className={`text-xl font-bold ${colorClass}`}>{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </DataCard>
              )}

              {/* Top Universities and Programs - Modern */}
              {(Object.keys(leadsMetrics.top_universities || {}).length > 0 || Object.keys(leadsMetrics.top_programs || {}).length > 0) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {Object.keys(leadsMetrics.top_universities || {}).length > 0 && (
                    <div className="bg-white/80 backdrop-blur-sm border border-gray-200 rounded-2xl p-6 shadow-lg">
                      <h3 className="text-lg font-bold text-gray-900 mb-5 flex items-center gap-2">
                        <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        Top Universities by Leads
                      </h3>
                      <div className="space-y-3">
                        {Object.entries(leadsMetrics.top_universities).slice(0, 5).map(([uni, count], idx) => (
                          <div key={uni} className="flex items-center justify-between p-3 bg-gradient-to-r from-indigo-50/50 to-purple-50/50 rounded-xl hover:shadow-md transition-all">
                            <div className="flex items-center gap-3">
                              <span className={`text-sm font-bold w-8 h-8 rounded-lg flex items-center justify-center ${
                                idx === 0 ? 'bg-gradient-to-br from-yellow-400 to-orange-500 text-white' :
                                idx === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400 text-white' :
                                idx === 2 ? 'bg-gradient-to-br from-amber-600 to-amber-700 text-white' :
                                'bg-gray-200 text-gray-600'
                              }`}>
                                {idx + 1}
                              </span>
                              <span className="text-sm font-medium text-gray-700">{uni}</span>
                            </div>
                            <span className="text-sm font-bold text-indigo-600 bg-indigo-100 px-3 py-1 rounded-lg">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {Object.keys(leadsMetrics.top_programs || {}).length > 0 && (
                    <div className="bg-white/80 backdrop-blur-sm border border-gray-200 rounded-2xl p-6 shadow-lg">
                      <h3 className="text-lg font-bold text-gray-900 mb-5 flex items-center gap-2">
                        <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                        Top Programs by Leads
                      </h3>
                      <div className="space-y-3">
                        {Object.entries(leadsMetrics.top_programs).slice(0, 5).map(([prog, count], idx) => (
                          <div key={prog} className="flex items-center justify-between p-3 bg-gradient-to-r from-purple-50/50 to-pink-50/50 rounded-xl hover:shadow-md transition-all">
                            <div className="flex items-center gap-3">
                              <span className={`text-sm font-bold w-8 h-8 rounded-lg flex items-center justify-center ${
                                idx === 0 ? 'bg-gradient-to-br from-yellow-400 to-orange-500 text-white' :
                                idx === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400 text-white' :
                                idx === 2 ? 'bg-gradient-to-br from-amber-600 to-amber-700 text-white' :
                                'bg-gray-200 text-gray-600'
                              }`}>
                                {idx + 1}
                              </span>
                              <span className="text-sm font-medium text-gray-700">{prog}</span>
                            </div>
                            <span className="text-sm font-bold text-purple-600 bg-purple-100 px-3 py-1 rounded-lg">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* SECTION B - WEBSITE SNAPSHOT (SECONDARY) - Modern */}
        {websiteSnapshot && websiteSnapshot.available && (
          <div className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-2xl p-6 mb-6 shadow-lg">
            <h2 className="text-xl font-bold text-gray-900 mb-5 flex items-center gap-2">
              <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Website Snapshot
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {[
                { label: 'Visitors Today', value: websiteSnapshot.visitors_today || 0, color: 'from-blue-500 to-indigo-600' },
                { label: 'Sessions Today', value: websiteSnapshot.sessions_today || 0, color: 'from-purple-500 to-pink-600' },
                { label: 'Avg Duration', value: `${websiteSnapshot.avg_session_duration || 0}s`, color: 'from-emerald-500 to-teal-600' },
                { label: 'Bounce Rate', value: `${websiteSnapshot.bounce_rate || 0}%`, color: 'from-orange-500 to-red-600' },
                { label: 'Top Source', value: websiteSnapshot.top_traffic_source || 'N/A', color: 'from-cyan-500 to-blue-600' }
              ].map((metric, idx) => (
                <div key={idx} className="bg-white/80 backdrop-blur-sm border border-gray-200 rounded-xl p-4 hover:shadow-lg transition-all">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">{metric.label}</p>
                  <p className={`text-2xl font-bold bg-gradient-to-r ${metric.color} bg-clip-text text-transparent`}>
                    {metric.value}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stats Cards - Using StatCard Component */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            label="Total Leads"
            value={stats.total}
            iconBg="bg-blue-100"
            iconColor="text-blue-600"
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            }
          />
          <StatCard
            label="New"
            value={stats.new}
            iconBg="bg-cyan-100"
            iconColor="text-cyan-600"
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
          <StatCard
            label="Contacted"
            value={stats.contacted}
            iconBg="bg-amber-100"
            iconColor="text-amber-600"
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            }
          />
          <StatCard
            label="Closed"
            value={stats.closed}
            iconBg="bg-emerald-100"
            iconColor="text-emerald-600"
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
        </div>

        {/* Quick Actions */}
        <QuickActions
          actions={[
            {
              label: 'Add University',
              icon: (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              ),
              onClick: () => navigate('/admin/universities/new'),
              variant: 'primary'
            },
            {
              label: 'Add Program',
              icon: (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              ),
              onClick: () => navigate('/admin/programs/new'),
              variant: 'primary'
            },
            {
              label: 'View Leads',
              icon: (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              ),
              onClick: () => navigate('/admin/leads'),
              variant: 'default'
            },
            {
              label: 'Analytics',
              icon: (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              ),
              onClick: () => navigate('/admin/analytics'),
              variant: 'default'
            }
          ]}
        />

        {/* Dashboard Grid - Stats and Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Submissions - Using DataCard */}
          <div className="lg:col-span-2">
            <DataCard
              title="Recent Submissions"
              actions={
                <button
                  onClick={() => navigate('/admin/leads')}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm rounded-lg flex items-center gap-2 transition-colors"
                  data-testid="view-all-button"
                >
                  View All
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              }
              padding="p-0"
            >

          {recentSubmissions.length === 0 ? (
            <EmptyState
              icon={
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              }
              title="No submissions yet"
              description="Form submissions will appear here once users start submitting forms."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50/50">
                    <th className="text-left py-4 px-6 text-xs font-bold text-gray-700 uppercase tracking-wider">Name</th>
                    <th className="text-left py-4 px-6 text-xs font-bold text-gray-700 uppercase tracking-wider">Type</th>
                    <th className="text-left py-4 px-6 text-xs font-bold text-gray-700 uppercase tracking-wider">Contact</th>
                    <th className="text-left py-4 px-6 text-xs font-bold text-gray-700 uppercase tracking-wider">Status</th>
                    <th className="text-left py-4 px-6 text-xs font-bold text-gray-700 uppercase tracking-wider">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {recentSubmissions.map((submission) => (
                    <tr
                      key={submission.id}
                      onClick={() => navigate(`/admin/leads/${submission.id}`)}
                      className="hover:bg-gradient-to-r hover:from-indigo-50/30 hover:to-purple-50/30 cursor-pointer transition-all"
                      data-testid={`submission-row-${submission.id}`}
                    >
                      <td className="py-4 px-6 text-sm font-semibold text-gray-900">{submission.full_name}</td>
                      <td className="py-4 px-6 text-sm text-gray-700">{getFormTypeLabel(submission.form_type)}</td>
                      <td className="py-4 px-6 text-sm text-gray-600">{submission.contact_number || submission.whatsapp_number || '-'}</td>
                      <td className="py-4 px-6">
                        <Badge variant={
                          (submission.lead_status || submission.status) === 'new' ? 'info' :
                          (submission.lead_status || submission.status) === 'contacted' ? 'warning' :
                          (submission.lead_status || submission.status) === 'closed' ? 'success' : 'default'
                        }>
                          {submission.lead_status || submission.status || 'new'}
                        </Badge>
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {new Date(submission.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DataCard>
          </div>

          {/* Activity Feed */}
          <div>
            <ActivityFeed
              activities={recentSubmissions.slice(0, 5).map(submission => ({
                type: 'create',
                user: submission.full_name || 'User',
                message: `submitted a ${getFormTypeLabel(submission.form_type)} form`,
                timestamp: new Date(submission.created_at).toLocaleString(),
                metadata: {
                  type: getFormTypeLabel(submission.form_type),
                  status: submission.lead_status || submission.status || 'new'
                }
              }))}
            />
          </div>
        </div>
      </PageShell>
    </AdminLayout>
  );
};

export default AdminDashboard;
